import React, {Fragment, useEffect, useMemo, useState} from "react";
import {
	Box,
	Button,
	CircularProgress,
	Collapse,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import dayjs from "dayjs";
import {
	DedicationAuditReport,
	DedicationAuditRestControllerApiFactory,
	DedicationEntry,
	MonthDedication,
	PersonDedication
} from "arteco-api-client-ts";
import {useApiManager} from "../../../../components/utils/api";
import {
	btnSx,
	EmptyState,
	FilterChips,
	Page,
	PageHeader,
	Panel,
	PersonAvatar,
	SkeletonRows,
	SoftChip,
	StatGrid,
	StatTile,
	tableContainerSx,
	tableSx,
	Tone,
	Toolbar,
} from "../../../../components/kit";
import {hours, shortDay} from "../../../employee/timeclock/timeclock_util";
import {exportDedicationAuditXls} from "../../../../components/utils/xlsExport";

type Scope = "all" | "rebate" | "mismatch";

const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre",
	"noviembre", "diciembre"];

/** «septiembre de 2026» a partir de «2026-09». */
const monthLabel = (ym?: string) => ym ? `${MONTHS[Number(ym.substring(5, 7)) - 1]} de ${ym.substring(0, 4)}` : "";

const numSx = {fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"} as const;

/** Por debajo de esto, redondeos: cuadra. */
const TOLERANCE = 0.05;

const sum = (months: MonthDedication[] | undefined, f: (m: MonthDedication) => number | undefined) =>
	(months || []).reduce((acc, m) => acc + (f(m) || 0), 0);

/** Faltan horas: rojo, es lo que tumba una bonificación. Sobran: ámbar, hay que explicarlas. */
const Difference = ({value}: { value: number }) => {
	if (Math.abs(value) < TOLERANCE) {
		return <SoftChip tone="success" label="Cuadra"/>;
	}
	return value < 0
		? <SoftChip tone="error" label={`Faltan ${hours(-value)}`}/>
		: <SoftChip tone="warning" label={`Sobran ${hours(value)}`}/>;
};

/** Horas imputadas en días sin jornada: si hay, ámbar, porque alguna fecha está mal. */
const DaysOff = ({value}: { value: number }) => value > 0 ? <SoftChip tone="warning" label={hours(value)}/> : <>{hours(0)}</>;

const Note = ({children}: { children: React.ReactNode }) => (
	<Stack direction="row" spacing={1} sx={{mt: 2, color: "text.secondary"}}>
		<InfoOutlinedIcon sx={{fontSize: 16, mt: "2px"}}/>
		<Typography variant="caption">{children}</Typography>
	</Stack>
);

/**
 * Dedicación de la plantilla (#14080): horas imputadas en Redmine, en todos los proyectos, frente a la
 * jornada del calendario de cada persona y sus ausencias. Es lo que RR. HH. cuadra para justificar las
 * bonificaciones a la Seguridad Social del personal investigador, sin ser miembro de ningún proyecto de
 * Redmine. Solo lectura.
 */
export const DedicationAuditPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(DedicationAuditRestControllerApiFactory);
	const [from, setFrom] = useState(dayjs().subtract(3, "month").format("YYYY-MM"));
	const [to, setTo] = useState(dayjs().subtract(1, "month").format("YYYY-MM"));
	const [scope, setScope] = useState<Scope>("all");
	const [report, setReport] = useState<DedicationAuditReport>();
	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [open, setOpen] = useState<number | undefined>();

	const load = (refresh: boolean) => {
		if (!from || !to) {
			return;
		}
		setLoading(true);
		setError(false);
		setReport(undefined);
		apiManager.execute(ctrl.dedicationAudit(from, to, false, refresh))
			.then(setReport)
			.catch(() => setError(true))
			.finally(() => setLoading(false));
	};

	useEffect(() => load(false), [from, to]);

	const people = report?.people || [];
	/** Descuadra si en algún mes falta o sobra, o si imputó en días sin jornada (fechas mal puestas). */
	const mismatch = (p: PersonDedication) => (p.months || [])
		.some(m => Math.abs(m.difference || 0) >= TOLERANCE || (m.hoursOnDaysOff || 0) > 0);
	const visible = useMemo(() => people.filter(p =>
		scope === "all" || (scope === "rebate" ? p.rebate : mismatch(p))), [report, scope]);

	const expected = visible.reduce((acc, p) => acc + sum(p.months, m => m.expectedHours), 0);
	const imputed = visible.reduce((acc, p) => acc + sum(p.months, m => m.imputedHours), 0);
	const daysOff = visible.reduce((acc, p) => acc + sum(p.months, m => m.hoursOnDaysOff), 0);
	const diff = imputed - expected;
	const diffTone: Tone = Math.abs(diff) < TOLERANCE ? "success" : diff < 0 ? "error" : "warning";

	const exportXls = () => {
		if (!report) {
			return;
		}
		setExporting(true);
		apiManager.execute(ctrl.dedicationAuditEntries(from, to))
			.then(entries => {
				const ids = new Set(visible.map(p => p.personId));
				const filtered = scope === "all" ? entries : entries.filter(e => e.personId && ids.has(e.personId));
				exportDedicationAuditXls({...report, people: visible}, filtered);
			})
			.finally(() => setExporting(false));
	};

	return (
		<Page>
			<PageHeader title="Dedicación"
						subtitle="Horas imputadas en Redmine, en todos los proyectos, frente a la jornada y las ausencias de cada persona"
						actions={<Stack direction="row" spacing={1}>
							<Button variant="outlined" startIcon={<RefreshRoundedIcon/>} sx={btnSx}
									disabled={loading} onClick={() => load(true)}>Releer Redmine</Button>
							<Button variant="contained" sx={btnSx} disabled={!report || exporting}
									startIcon={exporting ? <CircularProgress size={16} color="inherit"/> : <FileDownloadRoundedIcon/>}
									onClick={exportXls}>Descargar Excel</Button>
						</Stack>}/>

			<StatGrid>
				<StatTile icon={<ScheduleRoundedIcon/>} tone="info" label="Previstas" loading={loading} value={hours(expected)}
						  hint="Jornada menos ausencias aprobadas"/>
				<StatTile icon={<TaskAltRoundedIcon/>} tone="neutral" label="Imputadas" loading={loading} value={hours(imputed)}/>
				<StatTile icon={<CompareArrowsRoundedIcon/>} tone={diffTone} label="Diferencia" loading={loading}
						  value={`${diff > 0 ? "+" : ""}${hours(diff)}`}/>
				<StatTile icon={<EventBusyRoundedIcon/>} tone={daysOff > 0 ? "warning" : "success"} loading={loading}
						  label="En días sin jornada" value={hours(daysOff)} hint="Fines de semana, festivos y ausencias"/>
			</StatGrid>

			{(report?.unmatched || []).length > 0 && (
				<Panel title="Horas que no se atribuyen a nadie" icon={<ReportProblemRoundedIcon color="warning"/>}
					   subtitle="Están en Redmine pero su usuario no casa con ninguna ficha de persona: no cuentan en el cuadre.">
					<TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
						<Table size="small" sx={tableSx}>
							<TableHead><TableRow>
								<TableCell>Usuario de Redmine</TableCell><TableCell>Motivo</TableCell>
								<TableCell align="right">Horas</TableCell>
							</TableRow></TableHead>
							<TableBody>
								{report!.unmatched!.map(u => (
									<TableRow key={u.user}>
										<TableCell>{u.user}<Typography variant="caption" display="block" color="text.secondary">{u.mail}</Typography></TableCell>
										<TableCell>{u.reason}</TableCell>
										<TableCell align="right" sx={numSx}>{hours(u.hours)}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Panel>
			)}

			<Panel title="Cuadre por persona"
				   subtitle="Pulsa una persona para ver sus meses, y un mes para ver cada imputación.">
				<Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr) minmax(0, 1fr)", sm: "170px 170px"}, gap: 2, mb: 2}}>
					<TextField fullWidth size="small" type="month" label="Desde" value={from}
							   InputLabelProps={{shrink: true}} onChange={e => setFrom(e.target.value)}/>
					<TextField fullWidth size="small" type="month" label="Hasta" value={to}
							   InputLabelProps={{shrink: true}} onChange={e => setTo(e.target.value)}/>
				</Box>
				<Toolbar left={<FilterChips<Scope> value={scope} onChange={setScope} options={[
					{value: "all", label: "Toda la plantilla", count: people.length},
					{value: "rebate", label: "Bonificables I+D+i", count: people.filter(p => p.rebate).length},
					{value: "mismatch", label: "Con descuadre", count: people.filter(mismatch).length},
				]}/>}/>

				{loading ? (
					<>
						<SkeletonRows rows={6}/>
						<Note>Leyendo Redmine. La primera vez, un año entero puede tardar medio minuto; luego va al momento.</Note>
					</>
				) : error ? (
					<EmptyState title="No se ha podido cargar la dedicación"
								description="Revisa el rango (como mucho doce meses) y vuelve a intentarlo."
								action={<Button variant="outlined" sx={btnSx} onClick={() => load(false)}>Reintentar</Button>}/>
				) : visible.length === 0 ? (
					<EmptyState title="Nadie en ese filtro" description="Cambia el rango de meses o el filtro."/>
				) : (
					// Tabla ancha de cifras: en móvil se desplaza dentro de su caja.
					<TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
						<Table size="small" sx={{...tableSx, minWidth: 820}}>
							<TableHead>
								<TableRow>
									<TableCell sx={{width: 48}}/>
									<TableCell>Persona</TableCell>
									<TableCell align="right">Previstas</TableCell>
									<TableCell align="right">Ausencias</TableCell>
									<TableCell align="right">Imputadas</TableCell>
									<TableCell>Diferencia</TableCell>
									<TableCell align="right">En días sin jornada</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{visible.map(p => (
									<Fragment key={p.personId}>
										<TableRow hover sx={{cursor: "pointer"}}
												  onClick={() => setOpen(open === p.personId ? undefined : p.personId)}>
											<TableCell padding="checkbox">
												<IconButton size="small" aria-label="Ver meses">
													{open === p.personId ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
												</IconButton>
											</TableCell>
											<TableCell sx={{whiteSpace: "nowrap"}}>
												<Stack direction="row" spacing={1.5} alignItems="center">
													<PersonAvatar name={p.personName}/>
													<Box>
														<Typography variant="body2" fontWeight={600}>{p.personName}</Typography>
														{p.rebate && <Typography variant="caption" color="text.secondary">Bonificable I+D+i</Typography>}
													</Box>
												</Stack>
											</TableCell>
											<TableCell align="right" sx={numSx}>{hours(sum(p.months, m => m.expectedHours))}</TableCell>
											<TableCell align="right" sx={numSx}>{hours(sum(p.months, m => m.absenceHours))}</TableCell>
											<TableCell align="right" sx={{...numSx, fontWeight: 600}}>{hours(sum(p.months, m => m.imputedHours))}</TableCell>
											<TableCell><Difference value={sum(p.months, m => m.difference)}/></TableCell>
											<TableCell align="right" sx={numSx}><DaysOff value={sum(p.months, m => m.hoursOnDaysOff)}/></TableCell>
										</TableRow>
										<TableRow sx={{"&:hover": {bgcolor: "transparent"}}}>
											<TableCell sx={{py: 0, borderBottom: open === p.personId ? undefined : 0}} colSpan={7}>
												<Collapse in={open === p.personId} unmountOnExit>
													<PersonMonths person={p}/>
												</Collapse>
											</TableCell>
										</TableRow>
									</Fragment>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
				<Note>
					Las horas son las de Redmine en todos los proyectos, leídas con la clave de la Extranet. Solo restan las
					ausencias aprobadas; las pendientes se señalan aparte. «En días sin jornada» son horas imputadas en fin de
					semana, festivo o un día de ausencia completa: o la ausencia o la imputación tienen la fecha mal.
				</Note>
			</Panel>
		</Page>
	);
};

const PersonMonths = ({person}: { person: PersonDedication }) => {
	const [month, setMonth] = useState<string>();
	return (
		<Box sx={{my: 2}}>
			<TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
				<Table size="small" sx={{...tableSx, minWidth: 900}}>
					<TableHead>
						<TableRow>
							<TableCell>Mes</TableCell>
							<TableCell align="right">Laborables</TableCell>
							<TableCell align="right">Contrato</TableCell>
							<TableCell sx={{minWidth: 220}}>Ausencias</TableCell>
							<TableCell align="right">Previstas</TableCell>
							<TableCell align="right">Imputadas</TableCell>
							<TableCell>Diferencia</TableCell>
							<TableCell align="right">En días sin jornada</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{(person.months || []).map(m => (
							<Fragment key={m.month}>
								<TableRow hover sx={{cursor: "pointer"}} selected={month === m.month}
										  onClick={() => setMonth(month === m.month ? undefined : m.month)}>
									<TableCell sx={{whiteSpace: "nowrap", fontWeight: 600}}>{monthLabel(m.month)}</TableCell>
									<TableCell align="right" sx={numSx}>{m.workingDays}</TableCell>
									<TableCell align="right" sx={numSx}>{hours(m.contractHours)}</TableCell>
									<TableCell>
										<Stack spacing={0.5} alignItems="flex-start">
											{Object.entries(m.absences || {}).map(([label, days]) => (
												<Typography key={label} variant="body2">{label}: {days} {days === 1 ? "día" : "días"}</Typography>
											))}
											{(m.pendingAbsences || []).map(a => <SoftChip key={a} tone="warning" label={a}/>)}
											{m.warning && <SoftChip tone="error" label={m.warning}/>}
										</Stack>
									</TableCell>
									<TableCell align="right" sx={numSx}>{hours(m.expectedHours)}</TableCell>
									<TableCell align="right" sx={{...numSx, fontWeight: 600}}>{hours(m.imputedHours)}</TableCell>
									<TableCell><Difference value={m.difference || 0}/></TableCell>
									<TableCell align="right" sx={numSx}>
										<DaysOff value={m.hoursOnDaysOff || 0}/>
									</TableCell>
								</TableRow>
								{month === m.month && (
									<TableRow sx={{"&:hover": {bgcolor: "transparent"}}}>
										<TableCell colSpan={8} sx={{py: 1.5}}>
											<MonthEntries personId={person.personId!} month={m.month!}/>
										</TableCell>
									</TableRow>
								)}
							</Fragment>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};

const MonthEntries = ({personId, month}: { personId: number, month: string }) => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(DedicationAuditRestControllerApiFactory);
	const [entries, setEntries] = useState<DedicationEntry[]>();

	useEffect(() => {
		apiManager.execute(ctrl.dedicationAuditEntries(month, month, personId))
			.then(setEntries)
			.catch(() => setEntries([]));
	}, [personId, month]);

	if (!entries) {
		return <SkeletonRows rows={3} height={36}/>;
	}
	if (entries.length === 0) {
		return <Typography variant="body2" color="text.secondary">Sin imputaciones en Redmine este mes.</Typography>;
	}
	return (
		<TableContainer sx={tableContainerSx}>
			<Table size="small" sx={tableSx}>
				<TableHead>
					<TableRow>
						<TableCell>Fecha</TableCell>
						<TableCell>Proyecto</TableCell>
						<TableCell>Tarea</TableCell>
						<TableCell align="right">Horas</TableCell>
						<TableCell>Comentario</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{entries.map((e, i) => (
						<TableRow key={i}>
							<TableCell sx={numSx}>{shortDay(e.date)}</TableCell>
							<TableCell>{e.project}</TableCell>
							<TableCell>
								{e.issueId ? <><Typography variant="caption" color="text.secondary">#{e.issueId}</Typography> {e.issueSubject}</> : "—"}
							</TableCell>
							<TableCell align="right" sx={numSx}>{hours(e.hours)}</TableCell>
							<TableCell><Typography variant="body2" color="text.secondary">{e.comment}</Typography></TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
